import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWasteEnergy,
  addWasteToBusiness,
  updateWaste,
  deleteWaste,
} from "../../../../../../apis/Business/WasteApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const WasteEnergyTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownUsedFor, setDropdownUsedFor] = useState([
    {
      id: 1,
      name: t("WASTE_STEAM"),
      value: t("WASTE_STEAM"),
      type: t("WASTE_STEAM"),
      unit: "MJ",
      typeDB: "steam",
      key: "WASTE_STEAM",
    },
    {
      id: 2,
      name: t("WASTE_HEAT"),
      value: t("WASTE_HEAT"),
      type: t("WASTE_HEAT"),
      unit: "kWh",
      typeDB: "heat",
      key: "WASTE_HEAT",
    },
    {
      id: 3,
      name: t("WASTE_COOLING"),
      value: t("WASTE_COOLING"),
      type: t("WASTE_COOLING"),
      unit: "kWh",
      typeDB: "cooling",
      key: "WASTE_COOLING",
    },
  ]);

  useEffect(() => {
    setDropdownUsedFor([
      {
        id: 1,
        name: t("WASTE_STEAM"),
        value: t("WASTE_STEAM"),
        type: t("WASTE_STEAM"),
        unit: "MJ",
        typeDB: "steam",
        key: "WASTE_STEAM",
      },
      {
        id: 2,
        name: t("WASTE_HEAT"),
        value: t("WASTE_HEAT"),
        type: t("WASTE_HEAT"),
        unit: "kWh",
        typeDB: "heat",
        key: "WASTE_HEAT",
      },
      {
        id: 3,
        name: t("WASTE_COOLING"),
        value: t("WASTE_COOLING"),
        type: t("WASTE_COOLING"),
        unit: "kWh",
        typeDB: "cooling",
        key: "WASTE_COOLING",
      },
    ]);
  }, [t]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getWasteEnergy({
      businessId: businessId,
      setObjects: setData,
      category: "energy",
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      let typeObj = {};
      if (editedItem["type_surplus_energy.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_surplus_energy.value"].value
        );
      } else if (editedItem["type_surplus_energy"].value) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_surplus_energy"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type_surplus_energy &&
            typeof editedItem.type_surplus_energy === "string"
          ? editedItem.type_surplus_energy
          : data["typedb_surplus_energy"];
      updateWaste({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: "energy",
        type_surplus_energy: editedItem["type_surplus_energy.value"]
          ? editedItem["type_surplus_energy.value"].value
          : editedItem["type_surplus_energy"]
          ? editedItem["type_surplus_energy"].value
          : null,
        type_waste_water: null,
        type_surplus_material: null,
        type_current_disposal: null,
        typedb_surplus_energy: typedb,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, dropdownUsedFor]
  );

  const handleAdd = useCallback(
    (newItem) => {
      let typeObj = {};
      if (newItem["type_surplus_energy.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === newItem["type_surplus_energy.value"].value
        );
      }
      addWasteToBusiness({
        businessId,
        ...newItem,
        category: "energy",
        type_surplus_energy: newItem["type_surplus_energy.value"]
          ? newItem["type_surplus_energy.value"].value
          : null,
        type_waste_water: null,
        type_surplus_material: null,
        type_current_disposal: null,
        typedb_surplus_energy: typeObj ? typeObj.typeDB : newItem.type,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, dropdownUsedFor]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWaste({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "nickname",
        header: t("NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "type_surplus_energy.value",
        header: `${t("SURPLUS_ENERGY")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
        width: "110px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_SURPLUS_ENERGY"),
      },

      {
        accessor: "wasted_surplus_energy",
        header: `${t("QUANTITY_WASTED")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("WASTE_QTY"),
        width: "80px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_QTY_WASTED"),
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "sold_surplus_energy",
        header: t("QUANTITY_SOLD_SURPLUS_ENERGY"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_QTY_SURPLUS_ENERGY"),
      },

      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "wasted_energy",
        header: t("WASTED_ENERGY"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
        tipBody: t("TIPBOX_WASTED_ENERGY"),
        tipHeader: "Tip",
      },
      {
        accessor: "money_earned",
        header: t("MONEY_EARNED"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_MONEY_EARNED"),
      },
    ],
    [dropdownUsedFor, t]
  );

  const disableRules = {
    wasted_energy: () => true,
  };

  const subtotalColumns = {
    wasted_energy: { label: t("WASTED_ENERGY") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_surplus_energy.value": dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ wasted_energy: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default WasteEnergyTable;
